/**
 * IMPORTS
 */
import {ContactAlreadyExistsError}
    from 'src/aggregates/customers/err/contactalreadyexistserror';
import * as events from 'src/aggregates/customers/events';
import * as conversationsAPI from 'src/aggregates/conversations/api';
import {resetError as resetErrorAPI} from 'src/aggregates/customers/api';
import customers from 'src/aggregates/customers/selectors';
import {blockWhatsappContact} from 'src/aggregates/customers/utils';
import {contactTypesIdToChannel} from 'src/aggregates/customers/utils';
import {createCustomer} from 'src/aggregates/customers/utils';
import {ensureUniqueContact} from 'src/aggregates/customers/utils';
import {getContactTypes} from 'src/aggregates/customers/utils';
import {getCustomer} from 'src/aggregates/customers/utils';
import {getCustomersByTagPage} from 'src/aggregates/customers/utils';
import {getCustomersPage} from 'src/aggregates/customers/utils';
import {getMappedContactUids} from 'src/aggregates/customers/utils';
import {getUnassociatedCustomersToBeAdded}
    from 'src/aggregates/customers/utils';
import {getUnassociatedCustomersToBeRemoved}
    from 'src/aggregates/customers/utils';
import {isCustomerVisible} from 'src/aggregates/customers/utils';
import {reduceContact} from 'src/aggregates/customers/utils';
import {reduceContactNew} from 'src/aggregates/customers/utils';
import {removeCustomer} from 'src/aggregates/customers/utils';
import {syncBsuidsWithPhones} from 'src/aggregates/customers/utils';
import {tagCustomer} from 'src/aggregates/customers/utils';
import {unblockWhatsappContact} from 'src/aggregates/customers/utils';
import {untagCustomer} from 'src/aggregates/customers/utils';
import {updateCustomer} from 'src/aggregates/customers/utils';
import {validateWhatsappContacts} from 'src/aggregates/customers/utils';
import conversationsSelectors from 'src/aggregates/conversations/selectors';
import {updateKanbanMetadata} from 'src/aggregates/user/api';
import {showContactAlreadyExistsPopup} from 'src/utils/popup';


/**
 * TYPES
 */
import * as Commands from 'src/aggregates/customers/commands.d';
import {IContact} from 'src/aggregates/customers/state.d';
import {ICustomer} from 'src/aggregates/customers/state.d';
import {IAppState} from 'src/aggregates/index.d';
import {IEvent} from 'src/aggregates/index.d';


/**
 * CODE
 */

/**
 * I am an add customer command handler.
 *
 * :param command: add command
 * :param state: application state
 *
 * :generates: events
 */
async function* add (
    command: Commands.ICustomerAdd,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get the other unassociated customers that hold the added contacts
    const idsToRemove = getUnassociatedCustomersToBeRemoved(command.contacts)
        .filter(id => id !== command._target);

    yield events.customerAdded(
        command._target,
        command.agents,
        state.user.company,
        command.contacts,
        command.name,
        command.unassociated,
        command.address1,
        command.address2,
        command.city,
        command.code,
        command.description,
        command.document,
        command.email,
        command.organization,
        command.state,
        command.tags,
        command.zipcode,
    );

    // remove unassociated customers whose contact has been added to a customer
    yield* removeUnassociatedCustomers(command._target, idsToRemove);
}


/**
 * I am a drop siblings command handler.
 *
 * :param command: drop siblings command
 * :param state: application state
 *
 * :generates: events
 */
async function* dropSiblings (
    command: Commands.ICustomerDropSiblings,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.customerSiblingsDropped(command._target);
}


/**
 * I am an add customer search term command handler.
 *
 * :param command: add search term command
 * :param state: application state
 *
 * :generates: events
 */
async function* addSearchTerm (
    command: Commands.ICustomerAddSearchTerm,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.customerSearchTermAdded(command.term);
}


/*
 * I am a block whatsapp command handler.
 *
 * :param command: block whatsapp contact command
 * :param state: application state
 *
 * :generates: events
 */
async function* blockWhatsapp (
    command: Commands.ICustomerBlockWhatsappContact,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate a blocking whatsapp
    yield events.customerWhatsappBlocking();

    // block a whatsapp contact
    const response = await blockWhatsappContact(
        command.account,
        command.contact,
    );

    // request failed: generate failure event and abort
    if (response !== null)
    {
        yield events.customerWhatsappNotBlocked(response);
        return;
    }

    // generate custumer whatsapp blocked event
    yield events.customerWhatsappBlocked();
}


/*
 * I am a customer create command handler.
 *
 * :param command: create command
 * :param state: application state
 *
 * :generates: events
 */
async function* create (
    command: Commands.ICustomerCreate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate a creating customer event
    yield events.customerCreating();

    // validate whatsapp contacts
    const contacts = await validateWhatsappContacts(
        command.contacts.map(reduceContactNew),
    );

    // ensure contacts don't exist already
    try
    {
        await ensureUniqueContact(contacts, null);
    }

    // there are existing contacts: notify user, reset error and return
    catch (error)
    {
        // error is instance of contact already exists: show pop-up and
        // generate contact already exists event
        if (error instanceof ContactAlreadyExistsError)
        {
            showContactAlreadyExistsPopup(
                error.channel,
                error.customerId,
                error.customerName,
                error.uid,
            );
            yield events.customerContactAlreadyExists();
        }

        // reset error and return
        resetErrorAPI();
        return;
    }

    // create customer
    const result = await createCustomer(command.company,
                                        command.name,
                                        contacts.map(reduceContact),
                                        command.address1,
                                        command.address2,
                                        command.city,
                                        command.code,
                                        command.description,
                                        command.document,
                                        command.organization,
                                        command.state,
                                        command.zipcode);

    // create customer failed: generate customer not created event and stop
    if (result === null)
    {
        yield events.customerNotCreated();
        return;
    }

    // success: find created contacts that exists in unassociated customers
    const idsToRemove = getUnassociatedCustomersToBeRemoved(result.contacts);

    // success: generate a customer added event
    yield events.customerAdded(result.id,
                               result.agents,
                               result.company,
                               result.contacts,
                               result.name,
                               false,
                               result.address1,
                               result.address2,
                               result.city,
                               result.code,
                               result.description,
                               result.document,
                               result.email,
                               result.organization,
                               result.state,
                               [],
                               result.zipcode);

    // remove unassociated customers whose contact has been added to a customer
    for (const id of idsToRemove)
    {
        yield events.customerRemoved(id);
    }

    // generate a customer created event
    yield events.customerCreated(result.id);

    // get contact with conversation
    const ongoingContacts = result.contacts.find(contact =>
    {
        // get contact conversation
        const conversation = conversationsSelectors.getConversationByContact(
            contactTypesIdToChannel.get(contact.type_id).toLowerCase(),
            contact.value,
        );

        // return if contact has conversation
        return conversation !== null;
    });

    // set prioritize ongoing order
    const ongoingTop = ongoingContacts !== undefined ? [result.id] : [];

    // set prioritize history order
    const historyTop = ongoingContacts !== undefined ? [] : [result.id];

    // update lists with new customer data
    conversationsAPI.prioritizeOrder(
        [],
        historyTop,
        [],
        ongoingTop,
        idsToRemove,
    );
}


/*
 * I am a customer fetch command handler.
 *
 * :param command: fetch command
 * :param state: application state
 *
 * :generates: events
 */
async function* fetch (
    command: Commands.ICustomerFetch,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get unassociated prop from customer
    const unassociated = state.customer.byId.get(command._target)?.unassociated;

    // customer unassociated: return
    if (unassociated === true)
    {
        return;
    }

    // customer associated: fetch customer
    const result = await getCustomer(command._target);

    // fetch customer failed: return
    if (result === null)
    {
        return;
    }

    // reduce the fetched contacts
    const contacts = result.contacts.map(reduceContact);

    // get the other unassociated customers that hold the fetched contacts
    const idsToRemove = getUnassociatedCustomersToBeRemoved(contacts)
        .filter(id => id !== command._target);

    // fetch customer succeeded: generate customer fetched event
    yield events.customerFetched(
        result.address1,
        result.address2,
        result.agents,
        result.city,
        result.code,
        result.company,
        contacts,
        result.description,
        result.document,
        result.email,
        result.id,
        result.name,
        result.organization,
        result.state,
        result.tags,
        result.zipcode,
    );

    // remove unassociated customers whose contact entered the customer
    yield* removeUnassociatedCustomers(command._target, idsToRemove);
}


/*
 * I am a customer fetch contact types command handler.
 *
 * :param command: fetch contact types command
 * :param state: application state
 *
 * :generates: events
 */
async function* fetchContactTypes (
    command: Commands.ICustomerFetchContactTypes,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate a fetching contact types event
    yield events.customerContactTypesFetching();

    // fetch contact types
    const result = await getContactTypes();

    // fetch failed: generate customer contact types not fetched event and stop
    if (result === null)
    {
        yield events.customerContactTypesNotFetched();
        return;
    }

    // fetch succeeded: generate a customer contact types fetched event
    yield events.customerContactTypesFetched();

    // generate a customer contact type added event for each contact type
    for (const type of result)
    {
        yield events.customerContactTypeAdded(type.channel,
                                              type.id,
                                              type.name,
                                              type.prefix);
    }
}


/**
 * I am a customer focus command handler.
 *
 * :param command: focus command
 * :param state: application state
 *
 * :generates: events
 */
async function* focus (
    command: Commands.ICustomerFocus,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.customerFocused(command.customer);
}


/**
 * I am a fetch by tag page command handler.
 *
 * :param command: fetch by tag page command
 * :param state: application state
 *
 * :generates: events
 */
async function* fetchByTagPage (
    command: Commands.ICustomerFetchByTagPage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // no more pages: stop
    if (state.user.kanban.byTag[command.tag]?.hasMore === false)
    {
        return;
    }

    // set kanban loading
    updateKanbanMetadata(
        command.tag,
        {
            hasError: false,
            loading: true,
        },
    );

    // set customers per page
    const PER_PAGE = 15;

    // get current page
    const page = state.user.kanban.byTag[command.tag]?.page ?? 0;

    // fetch customers
    const result = await getCustomersByTagPage(
        command.tag,
        page + 1,
        PER_PAGE,
    );

    // failed: set kanban error and stop
    if (result === null)
    {
        updateKanbanMetadata(
            command.tag,
            {
                hasError: true,
                loading: false,
            },
        );
        return;
    }

    // initialize customers ids
    const ids: number[] = [];

    // for each customer: fetch, push to ids list and save on store
    for (const id of result)
    {
        // get customer data from store
        const stored = customers.fetch(id) ?? null;

        // customer is on store: push id to list and continue
        if (stored !== null)
        {
            ids.push(id);
            continue;
        }

        // fetch customer data
        const customer = await getCustomer(id);

        // failed: set kanban error and stop
        if (customer === null)
        {
            updateKanbanMetadata(
                command.tag,
                {
                    hasError: true,
                    loading: false,
                },
            );
            return;
        }

        // push id to list
        ids.push(id);

        // save customer data on store
        yield events.customerAdded(
            customer.id,
            customer.agents,
            customer.company,
            customer.contacts.map(reduceContact),
            customer.name,
            false,
            customer.address1,
            customer.address2,
            customer.city,
            customer.code,
            customer.description,
            customer.document,
            customer.email,
            customer.organization,
            customer.state,
            customer.tags,
            customer.zipcode,
        );
    }

    // save kanban metadata
    updateKanbanMetadata(
        command.tag,
        {
            customers: [
                ...state.user.kanban.byTag[command.tag]?.customers,
                ...ids,
            ],
            hasError: false,
            hasMore: ids.length === PER_PAGE,
            loading: false,
            page: page + 1,
        },
    );
}


/**
 * I am a customer load page command handler.
 *
 * :param command: customer load page command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadPage(
    command: Commands.ICustomerLoadPage,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get customers props
    const {hasMorePages, isLoading, page} = state.customer;

    // is loading customers or has no more to load: return
    if (isLoading === true || hasMorePages !== true)
    {
        return;
    }

    // generate customer loading event
    yield events.customerLoading();

    // get next page
    const nextPage = page + 1;

    // load customers page
    const customers = await getCustomersPage(nextPage);

    // failed to load customers: generate error event and return
    if (customers === null)
    {
        yield events.customerNotLoaded();
        return;
    }

    // all pages loaded: generate customer all pages loaded and return
    if (customers.length === 0)
    {
        yield events.customerAllPagesLoaded();
        return;
    }

    // get customers to add
    const customersToAdd = customers.filter(
        customer => isCustomerVisible(customer.agents),
    );

    // format customers and contacts
    const formattedCustomers = customersToAdd.map((customer): ICustomer => ({
        ...customer,
        calls: {},
        contacts: customer.contacts.map(reduceContact),
        unassociated: false,
    }));

    // generate customers page loaded event
    yield events.customerPageLoaded(formattedCustomers, nextPage);
}

/**
 * I am a load siblings command handler.
 *
 * :param command: load siblings command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadSiblings (
    command: Commands.ICustomerLoadSiblings,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // mark the siblings of the contact as loading
    yield events.customerSiblingsLoading(command._target);

    // get the uids the contact is also known by
    const siblings = await getMappedContactUids(command.typeId, command.uid);

    // set the siblings of the contact
    yield events.customerSiblingsLoaded(command._target, siblings);
}


/**
 * I am a modify customer command handler.
 *
 * :param command: modify command
 * :param state: application state
 *
 * :generates: events
 */
async function* modify (
    command: Commands.ICustomerModify,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // get the email held in the store, which the command does not carry
    const email = customers.fetch(command._target)?.email;

    yield events.customerUpdated(
        command.address1,
        command.address2,
        command.agents,
        command.city,
        command.code,
        state.user.company,
        command.contacts,
        command.document,
        email,
        command._target,
        command.name,
        command.description,
        command.organization,
        command.state,
        command.zipcode,
        command.tags,
    );
}


/**
 * I am an update customer command handler.
 *
 * :param command: update command
 * :param state: application state
 *
 * :generates: events
 */
async function* update (
    command: Commands.ICustomerUpdate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate updating event
    yield events.customerUpdating();

    // get customer old contacts
    const oldContacts = customers.fetch(command._target).contacts;

    // update contacts of whatsapp type
    const validated = await validateWhatsappContacts(command.contacts);

    // make the bsuids follow the phones the update adds or removes
    const {contacts, phonesByBsuid} = await syncBsuidsWithPhones(oldContacts,
                                                                 validated);

    // get the contacts the update adds to the customer
    const incoming = contacts.filter(contact => oldContacts.some(
        held => held.value === contact.uid) === false);

    // ensure the added contacts don't belong to another customer
    try
    {
        // update ensures unique contacts: ensure the incoming ones are unique
        if (command.ensureUniqueContacts === true)
        {
            await ensureUniqueContact(incoming, command._target);
        }
    }

    // there are existing contacts: notify user, reset error and return
    catch (error)
    {
        // error is instance of contact already exists: show pop-up and
        // generate contact already exists event
        if (error instanceof ContactAlreadyExistsError)
        {
            showContactAlreadyExistsPopup(
                error.channel,
                error.customerId,
                error.customerName,
                phonesByBsuid.get(error.uid) ?? error.uid,
            );
            yield events.customerContactAlreadyExists();
        }

        // reset error and return
        resetErrorAPI();
        return;
    }

    // update customer
    const customer = await updateCustomer(command.address1,
                                          command.address2,
                                          command.agents,
                                          command.city,
                                          command.code,
                                          command.company,
                                          contacts,
                                          command.document,
                                          command._target,
                                          command.name,
                                          command.description,
                                          command.organization,
                                          command.state,
                                          command.zipcode);

    // customer is null: generate update failed event and stop
    if (customer === null)
    {
        yield events.customerNotUpdated();
        return;
    }

    // find contacts that have been added
    const addedContacts: IContact[] = [];
    customer.contacts.forEach ((contact: IContact): void =>
    {
        // check if contact was added
        const filtered = oldContacts.filter((oldContact: IContact): boolean => (
            oldContact.type_id === contact.type_id &&
            oldContact.value === contact.value
        ));

        // contact has been added: add it to addedContacts array
        if (filtered.length === 0)
        {
            addedContacts.push(contact);
        }
    });

    // find contacts that have been removed
    const removedContacts: IContact[] = [];
    oldContacts.forEach ((contact: IContact): void =>
    {
        // check if contact was removed
        const filtered = customer.contacts.filter(
            (updatedContact: IContact): boolean => (
                updatedContact.type_id === contact.type_id &&
                updatedContact.value === contact.value
            ),
        );

        // contact has been removed: add it to removed contacts array
        if (filtered.length === 0)
        {
            removedContacts.push(contact);
        }
    });

    // get id of unassociated customers that need to be removed
    const idsToRemove: (string | number)[] =
        getUnassociatedCustomersToBeRemoved(addedContacts);

    // get unassociated customer that need to be added
    const customersToAdd = await getUnassociatedCustomersToBeAdded(
        removedContacts,
    );

    // create unassociated customers
    const ongoing: (number | string)[] = [];
    for (const customer of customersToAdd)
    {
        yield events.customerAdded(
            customer.id,
            customer.agents,
            customer.company,
            customer.contacts,
            customer.name,
            customer.unassociated,
        );

        // unassociated customer has a conversation: add it to ongoing
        const hasConversation = customer.contacts.some(contact =>
            conversationsSelectors.getConversationByContact(
                contactTypesIdToChannel.get(contact.type_id).toLowerCase(),
                contact.value,
            ) !== null);
        if (hasConversation === true)
        {
            ongoing.push(customer.id);
        }
    }

    // update customer succeeded: generate update succeeded event
    yield events.customerUpdated(customer.address1,
                                 customer.address2,
                                 customer.agents,
                                 customer.city,
                                 customer.code,
                                 customer.company,
                                 customer.contacts,
                                 customer.document,
                                 customer.email,
                                 customer.id,
                                 customer.name,
                                 customer.description,
                                 customer.organization,
                                 customer.state,
                                 customer.zipcode);

    // remove fake customers whose contact is present on updated customer
    for (const id of idsToRemove)
    {
        yield events.customerRemoved(id);
    }

    // get contact with conversation
    const ongoingContacts = customer.contacts.find(contact =>
    {
        // get contact conversation
        const conversation = conversationsSelectors.getConversationByContact(
            contactTypesIdToChannel.get(contact.type_id)?.toLowerCase(),
            contact.value,
        );

        // return if contact has conversation
        return conversation !== null;
    });

    // set prioritize ongoing order
    const ongoingTop = ongoingContacts !== undefined ? [customer.id] : [];

    // set prioritize history order
    const historyTop = ongoingContacts !== undefined ? [] : [customer.id];

    // update lists with new customer data
    conversationsAPI.prioritizeOrder(
        [],
        historyTop,
        ongoing,
        ongoingTop,
        idsToRemove,
    );

}


/**
 * I am a remove customer command handler.
 *
 * :param command: remove customer command
 * :param state: application state
 *
 * :generates: events
 */
async function* remove (
    command: Commands.ICustomerRemove,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate removing customer event
    yield events.customerRemoving(command._target);

    // remove customer
    const result = await removeCustomer(command._target);

    // remove customer failed: generate customer not removed event and stop
    if (result === null)
    {
        yield events.customerNotRemoved(command._target);
        return;
    }

    // remove customer succeeded: get new unassociated customer for each contact
    const customersToAdd = await getUnassociatedCustomersToBeAdded (
        customers.fetch(command._target).contacts,
    );

    // create unassociated customers
    const ongoing: (number | string)[] = [];
    for (const customer of customersToAdd)
    {
        yield events.customerAdded(
            customer.id,
            customer.agents,
            customer.company,
            customer.contacts,
            customer.name,
            customer.unassociated,
        );

        // unassociated customer has a conversation: add it to ongoing
        const hasConversation = customer.contacts.some(contact =>
            conversationsSelectors.getConversationByContact(
                contactTypesIdToChannel.get(contact.type_id).toLowerCase(),
                contact.value,
            ) !== null);
        if (hasConversation === true)
        {
            ongoing.push(customer.id);
        }
    }

    // generate customer removed event
    yield events.customerRemoved(command._target);

    // update lists with new customer data
    conversationsAPI.prioritizeOrder([], [], ongoing, [], [command._target]);
}


/**
 * I remove unassociated customers and move their conversations to a customer.
 *
 * :param customerId: customer id
 * :param idsToRemove: ids of the unassociated customers to remove
 *
 * :generates: events
 */
async function* removeUnassociatedCustomers (
    customerId: ICustomer['id'],
    idsToRemove: ICustomer['id'][],
): AsyncIterable<IEvent>
{
    // generate customer removed event for each unassociated customer
    for (const idToRemove of idsToRemove)
    {
        yield events.customerRemoved(idToRemove);
    }

    // no unassociated customer removed: return
    if (idsToRemove.length === 0)
    {
        return;
    }

    // get whether the customer has a conversation
    const hasConversation = conversationsSelectors
        .getConversationsByCustomer(customerId).length > 0;

    // set prioritize ongoing order
    const ongoingTop = hasConversation === true ? [customerId] : [];

    // set prioritize history order
    const historyTop = hasConversation === true ? [] : [customerId];

    // move the conversations of the removed customers to the customer
    conversationsAPI.prioritizeOrder([],
                                     historyTop,
                                     [],
                                     ongoingTop,
                                     idsToRemove);
}


/**
 * I am a reset command handler.
 *
 * :generates: events
 */
async function* resetError (
    command: Commands.ICustomerResetError,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.customerErrorReset();
}


/**
 * I am a customer tag command handler.
 *
 * :generates: events
 */
async function* tag (
    command: Commands.ICustomerTag,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate customer tagging event
    yield events.customerTagging(command._target);

    // tag customer
    const customerTag = await tagCustomer(command._target,  command.tag);

    // customer tag failed: generate customer not tagged event
    if (customerTag === null)
    {
        yield events.customerNotTagged(command._target);
        return;
    }

    // customer tag success: generate customer tagged event
    yield events.customerTagged(command._target, customerTag);
}


/*
 * I am a unblock whatsapp command handler.
 *
 * :param command: unblock whatsapp contact command
 * :param state: application state
 *
 * :generates: events
 */
async function* unblockWhatsapp (
    command: Commands.ICustomerUnblockWhatsappContact,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate an unblocking whatsapp
    yield events.customerWhatsappUnblocking();

    // unblock whatsapp contact
    const response = await unblockWhatsappContact(
        command.account,
        command.contact,
    );

    // request failed: generate failure event and abort
    if (response !== null)
    {
        yield events.customerWhatsappNotUnblocked(response);
        return;
    }

    // generate custumer whatsapp unblocked event
    yield events.customerWhatsappUnblocked();
}


/**
 * I am a customer untag command handler.
 *
 * :generates: events
 */
async function* untag (
    command: Commands.ICustomerUntag,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate customer tagging event
    yield events.customerTagging(command._target);

    // untag customer
    const result = await untagCustomer(command.customerTag);

    // customer untag failed: generate customer not untagged event
    if (result === null)
    {
        yield events.customerNotUntagged(command._target);
        return;
    }

    // customer untag success: generate customer untagged event
    yield events.customerUntagged(command._target, result.id);
}


/**
 * I am a customer update tags filter handler.
 *
 * :generates: events
 */
async function* updateTagsFilter (
    command: Commands.ICustomerUpdateTagsFilter,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate customer tags filter updated event
    yield events.customerTagsFilterUpdated(command.tags);
}


/**
 * EXPORTS
 */
export {
    add,
    addSearchTerm,
    blockWhatsapp,
    create,
    dropSiblings,
    fetch,
    fetchByTagPage,
    fetchContactTypes,
    focus,
    loadPage,
    loadSiblings,
    modify,
    remove,
    resetError,
    tag,
    unblockWhatsapp,
    untag,
    update,
    updateTagsFilter,
};
